import { useCallback, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import { Cart, Discount } from 'shared/types/cart';
import {
  AlertCode,
  CartResponse,
  RedeemDiscountPayload,
  RedeemDiscountResponse,
  RemoveDiscountPayload,
  RemoveDiscountResponse,
  RemoveLineItemResponse,
} from 'shared/types/checkout';
import { sdk } from 'sdk';
import {
  getAccountId,
  getIsCTADisabled,
  setAlert,
  useAppDispatch,
  useAppSelector,
} from 'store';
import {
  getProductsFromLineItems,
  makeSDKCallWithRetries,
  sendGTMEvent,
} from 'utils';
import { withCookieValidation } from 'utils/edx-auth';
import { elementEvents, ElementType, ElementViewed } from 'utils/eventing';
import { centsToDollars, sumMoney } from 'utils/price';
import { revalidateOptions } from '../options';

const getCart = (customerId: string) =>
  withCookieValidation(
    async () =>
      makeSDKCallWithRetries('cart/getCart', customerId, () =>
        sdk.callAction<CartResponse>({
          actionName: 'cart/getCart',
        }),
      ),
    true,
    customerId,
  );

export const useCart = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);
  const isCTADisabled = useAppSelector(getIsCTADisabled);
  const result = useSWR('/action/cart/getCart', () => getCart(customerId), {
    ...revalidateOptions,
    // Disable auto revalidation during order processing or coupon application.
    ...(isCTADisabled && { revalidateOnFocus: false }),
  });
  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  const data = (
    result.data?.isError || result.data?.data?.isError ? {} : result.data?.data
  ) as Partial<Cart> | undefined;

  const isEmpty = data && 'lineItems' in data ? !data.lineItems?.length : true;
  const cartError = result.data?.isError
    ? result.data.error
    : result.data?.data?.isError
      ? result.data.data.error
      : null;

  const redeemDiscountCode = useCallback(
    (cartID: string) =>
      async (
        code: string,
        showSuccessMessage = true,
        showErrorMessage = true,
      ) => {
        const payload: RedeemDiscountPayload = {
          cartID,
          code,
        };
        const response = await withCookieValidation(() =>
          makeSDKCallWithRetries(
            'cart/redeemDiscount',
            customerId,
            () =>
              sdk.callAction<RedeemDiscountResponse>({
                actionName: 'cart/redeemDiscount',
                payload,
              }),
            {
              retryErrorCodes: ['ConcurrentModification'],
            },
          ),
        );

        let errorCode: string | undefined;

        if (response.isError) {
          errorCode = response.error?.code as string | undefined;
          console.error(
            `Error in redeemDiscount extension for customer: ${customerId} for discount code: ${code} with error code: ${(response.error?.code as string) ?? ''} and error message: ${response.error?.message ?? JSON.stringify(response.error) ?? 'unknown'}`,
          );
          if (showErrorMessage) {
            dispatch(setAlert({ code: AlertCode.FALLBACK }));
          }
        } else if (response.data.isError) {
          errorCode = response.data.error.code;
          if (response.data.error.code === 'cart_changed_error') {
            if (showErrorMessage) {
              dispatch(setAlert({ code: AlertCode.CART_CHANGED }));
            }
          } else if (
            Object.values(AlertCode).includes(
              response.data.error.code as AlertCode,
            )
          ) {
            await mutate('/action/cart/getCart');
            if (showErrorMessage) {
              dispatch(
                setAlert({
                  code: response.data.error.code as AlertCode,
                  value: code,
                }),
              );
            }
          } else {
            await mutate('/action/cart/getCart');

            if (showErrorMessage) {
              dispatch(
                setAlert({
                  code: AlertCode.FALLBACK,
                  value: code,
                }),
              );
            }
          }
          console.error(
            `Error in redeemDiscount extension for customer: ${customerId} for discount code: ${code} with error code: ${response.data.error.code ?? ''} and error message: ${response.data.error.message ?? JSON.stringify(response.data.error) ?? 'unknown'}`,
          );
        } else if (response.data.code) {
          const { discountOnTotalPrice, lineItems, discountOnLineItems } =
            response.data.cart;
          sendGTMEvent({
            event: 'Coupon Applied',
            properties: {
              track_plan_id: 30,
              trigger_source: 'client-side',
              cart_id: cartID,
              checkout_id: cartID,
              coupon_name: response.data.code,
              discount: centsToDollars(
                sumMoney(discountOnTotalPrice, discountOnLineItems),
              ),
              currency: discountOnTotalPrice?.currencyCode ?? 'USD',
              products: getProductsFromLineItems(lineItems),
            },
          });

          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          mutate(
            '/action/cart/getCart',
            {
              isError: response.data.isError ?? false,
              data: response.data.cart,
            },
            {
              optimisticData: {
                isError: response.data.isError ?? false,
                data: response.data.cart,
              },
              revalidate: false,
              populateCache: true,
            },
          );

          if (showSuccessMessage) {
            dispatch(
              setAlert({
                code: AlertCode.REDEEM_DISCOUNT,
                value: response.data.code,
              }),
            );
          }

          const paypalTagularElement: ElementViewed['webElement'] = {
            elementType: ElementType.Button,
            name: 'promotional-code',
            text: 'Apply',
          };

          const { elementViewed } = elementEvents({
            webElement: paypalTagularElement,
          });
          elementViewed();

          return {
            isError: false,
            discounts: response.data.cart.discountCodes,
          };
        } else {
          if (showErrorMessage) {
            dispatch(setAlert({ code: AlertCode.FALLBACK }));
          }
          console.error(
            `Error in redeemDiscount extension for customer: ${customerId} and discount code: ${code}.`,
          );
        }

        return {
          isError: true,
          discounts: null,
          errorCode,
        };
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  const removeDiscountCode = useCallback(
    (cartID: string) =>
      async (
        discount: Discount,
        message: {
          type?: AlertCode;
          value?: string;
        } = {},
        isSilent = false,
      ) => {
        const payload: RemoveDiscountPayload = {
          cartID,
          discount,
        };
        const response = await withCookieValidation(() =>
          sdk.callAction<RemoveDiscountResponse>({
            actionName: 'cart/removeDiscount',
            payload,
          }),
        );
        if (response.isError) {
          console.error(
            `Error in removeDiscount extension for customer: ${customerId} and discount code: ${discount.code!} with error code: ${(response.error?.code as string) ?? ''} and error message: ${response.error?.message ?? JSON.stringify(response.error) ?? 'unknown'}`,
          );

          dispatch(setAlert({ code: AlertCode.FALLBACK }));
        } else if (response.data.isError) {
          if (response.data.error.code === 'cart_changed_error') {
            dispatch(setAlert({ code: AlertCode.CART_CHANGED }));
          } else {
            dispatch(setAlert({ code: AlertCode.FALLBACK }));
          }
          console.error(
            `Error in removeDiscount extension for customer: ${customerId} and discount code: ${discount.code!} with error code: ${response.data.error?.code ?? ''} error message: ${response.data.error.message ?? JSON.stringify(response.data.error) ?? 'unknown'}`,
          );
        } else if (response.data.cart) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          mutate(
            '/action/cart/getCart',
            {
              isError: response.data.isError ?? false,
              data: response.data.cart,
            },
            {
              optimisticData: {
                isError: response.data.isError ?? false,
                data: response.data.cart,
              },
              revalidate: false,
              populateCache: true,
            },
          );
          if (!isSilent) {
            dispatch(
              setAlert({
                code: message?.type ?? AlertCode.REMOVE_DISCOUNT,
                value: message?.value ?? discount?.code,
              }),
            );
          }
        } else {
          dispatch(setAlert({ code: AlertCode.FALLBACK }));
          console.error(
            `Error in removeDiscount extension for customer: ${customerId} and discount code: ${discount.code!}.`,
          );
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  const removeItem = useCallback(
    async (lineItemId: string, productName?: string) => {
      if (!data?.cartId) {
        console.error('No cart ID available for removing item');
        return;
      }

      // Validate that the line item exists in the current cart
      const lineItem = data.lineItems?.find(
        (item) => item.lineItemId === lineItemId,
      );
      if (!lineItem) {
        console.error(
          `Line item ${lineItemId} not found in cart for customer: ${customerId}`,
        );
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
        return;
      }

      const payload = {
        cartID: data.cartId,
        lineItemId,
      };

      const response = await withCookieValidation(() =>
        makeSDKCallWithRetries(
          'cart/removeLineItem',
          customerId,
          () =>
            sdk.callAction<RemoveLineItemResponse>({
              actionName: 'cart/removeLineItem',
              payload,
            }),
          {
            retryErrorCodes: ['ConcurrentModification'],
          },
        ),
      );

      if (response.isError) {
        console.error(
          `Error removing item from cart for customer: ${customerId} and lineItemId: ${lineItemId} with error code: ${(response.error?.code as string) ?? ''} and error message: ${response.error?.message ?? JSON.stringify(response.error) ?? 'unknown'}`,
        );
        console.error('Full error response:', response.error);
        console.error('Cart data at time of error:', data);
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
      } else if (response.data?.isError) {
        if (response.data.error?.code === 'cart_changed_error') {
          dispatch(setAlert({ code: AlertCode.CART_CHANGED }));
        } else {
          dispatch(setAlert({ code: AlertCode.FALLBACK }));
        }
        console.error(
          `Error removing item from cart for customer: ${customerId} and lineItemId: ${lineItemId} with error code: ${response.data.error?.code ?? ''} error message: ${response.data.error?.message ?? JSON.stringify(response.data.error) ?? 'unknown'}`,
        );
      } else if (response.data?.cart) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        mutate(
          '/action/cart/getCart',
          {
            isError: response.data.isError ?? false,
            data: response.data.cart,
          },
          {
            optimisticData: {
              isError: response.data.isError ?? false,
              data: response.data.cart,
            },
            revalidate: false,
            populateCache: true,
          },
        );
        dispatch(
          setAlert({
            code: AlertCode.ITEM_REMOVED,
            value: productName,
          }),
        );
      } else {
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
        console.error(
          `Error removing item from cart for customer: ${customerId} and lineItemId: ${lineItemId}.`,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, data?.cartId],
  );

  const removeProgram = useCallback(
    async (lineItemId: string, productName?: string) => {
      if (!data?.cartId) {
        console.error('No cart ID available for removing program');
        return;
      }

      // Validate that the line item exists in the current cart
      const lineItem = data.lineItems?.find(
        (item) => item.lineItemId === lineItemId,
      );
      if (!lineItem) {
        console.error(
          `Line item ${lineItemId} not found in cart for customer: ${customerId}`,
        );
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
        return;
      }

      const payload = {
        cartID: data.cartId,
        lineItemId,
      };

      const response = await withCookieValidation(() =>
        makeSDKCallWithRetries(
          'cart/removeProgramFromCart',
          customerId,
          () =>
            sdk.callAction<RemoveLineItemResponse>({
              actionName: 'cart/removeProgramFromCart',
              payload,
            }),
          {
            retryErrorCodes: ['ConcurrentModification'],
          },
        ),
      );

      if (response.isError) {
        console.error(
          `Error removing program from cart for customer: ${customerId} and lineItemId: ${lineItemId} with error code: ${(response.error?.code as string) ?? ''} and error message: ${response.error?.message ?? JSON.stringify(response.error) ?? 'unknown'}`,
        );
        console.error('Full error response:', response.error);
        console.error('Cart data at time of error:', data);
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
      } else if (response.data?.isError) {
        if (response.data.error?.code === 'cart_changed_error') {
          dispatch(setAlert({ code: AlertCode.CART_CHANGED }));
        } else {
          dispatch(setAlert({ code: AlertCode.FALLBACK }));
        }
        console.error(
          `Error removing program from cart for customer: ${customerId} and lineItemId: ${lineItemId} with error code: ${response.data.error?.code ?? ''} error message: ${response.data.error?.message ?? JSON.stringify(response.data.error) ?? 'unknown'}`,
        );
      } else if (response.data?.cart) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        mutate(
          '/action/cart/getCart',
          {
            isError: response.data.isError ?? false,
            data: response.data.cart,
          },
          {
            optimisticData: {
              isError: response.data.isError ?? false,
              data: response.data.cart,
            },
            revalidate: false,
            populateCache: true,
          },
        );
        dispatch(
          setAlert({
            code: AlertCode.ITEM_REMOVED,
            value: productName,
          }),
        );
      } else {
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
        console.error(
          `Error removing program from cart for customer: ${customerId} and lineItemId: ${lineItemId}.`,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, data?.cartId],
  );

  useEffect(() => {
    if (cartError && cartError.code !== 'ResourceNotFound') {
      console.error(
        `Error getting cart with product for customer: ${customerId} with error code: ${(cartError.code as string) ?? ''} and error message ${cartError.message ?? JSON.stringify(cartError) ?? 'unknown'}`,
      );
      dispatch(setAlert({ code: AlertCode.CART_LOAD_FAILED }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartError, customerId]);

  return {
    data,
    redeemDiscountCode,
    removeDiscountCode,
    removeItem,
    removeProgram,
    isEmpty,
    isLoading: result.isLoading,
  };
};
